/**
 * Shared behaviour for anything that floats above the page: dialogs, sheets, menus, popovers, the mobile nav drawer.
 *
 *   useLayer(open)                      registers the overlay on a stack while open; returns isTop()
 *   useEscape(open, onClose, isTop)     Escape closes only the top-most overlay, so a menu inside a dialog closes
 *                                       first and the dialog stays
 *   useScrollLock(active)               ref-counted body scroll lock; two dialogs open at once unlock only when
 *                                       both close, and a closed dialog never touches body styles
 *   useFocusTrap(ref, active, opts)     moves focus in on open, keeps Tab inside, returns focus to the opener
 *   useMediaQuery(query)                SSR-safe matchMedia
 *
 * Primitives (Dialog, Sheet, DropdownMenu) already use these; reach for them directly only for a custom overlay.
 */
import { RefObject, useCallback, useEffect, useId, useRef, useState } from "react";

// ---------- layer stack ----------
const stack: string[] = [];

export function useLayer(open: boolean) {
  const id = useId();
  useEffect(() => {
    if (!open) return;
    stack.push(id);
    return () => {
      const i = stack.lastIndexOf(id);
      if (i !== -1) stack.splice(i, 1);
    };
  }, [open, id]);
  return useCallback(() => stack[stack.length - 1] === id, [id]);
}

export function useEscape(open: boolean, onClose: () => void, isTop: () => boolean) {
  const cb = useRef(onClose);
  cb.current = onClose;
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || !isTop()) return;
      e.stopPropagation();
      cb.current();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, isTop]);
}

// ---------- scroll lock ----------
let lockCount = 0;
let savedOverflow = "";
let savedPaddingRight = "";

export function useScrollLock(active: boolean) {
  useEffect(() => {
    if (!active || typeof document === "undefined") return;
    const body = document.body;
    if (lockCount === 0) {
      savedOverflow = body.style.overflow;
      savedPaddingRight = body.style.paddingRight;
      const gap = window.innerWidth - document.documentElement.clientWidth;
      if (gap > 0) body.style.paddingRight = `${gap}px`;
      body.style.overflow = "hidden";
    }
    lockCount++;
    return () => {
      lockCount = Math.max(0, lockCount - 1);
      if (lockCount === 0) {
        body.style.overflow = savedOverflow;
        body.style.paddingRight = savedPaddingRight;
      }
    };
  }, [active]);
}

// ---------- focus trap ----------
const FOCUSABLE =
  'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [contenteditable="true"], [tabindex]:not([tabindex="-1"])';

export function getFocusable(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    el => !el.hasAttribute("inert") && el.getClientRects().length > 0 && !el.closest("[inert]")
  );
}

interface FocusTrapOptions {
  /** Element to focus first. Defaults to the first [data-autofocus] element, then the first focusable one. */
  initialFocus?: RefObject<HTMLElement | null>;
  /** Return focus to whatever had it before opening. Default true. */
  returnFocus?: boolean;
  /** Only trap while this overlay is the top layer, so a menu opened from a dialog can take focus. */
  isTop?: () => boolean;
}

export function useFocusTrap(ref: RefObject<HTMLElement | null>, active: boolean, opts: FocusTrapOptions = {}) {
  const { initialFocus, returnFocus = true, isTop } = opts;
  useEffect(() => {
    if (!active) return;
    const opener = document.activeElement as HTMLElement | null;
    // Wait a frame so portal content and enter animations have mounted.
    const raf = requestAnimationFrame(() => {
      const root = ref.current;
      if (!root) return;
      const target =
        initialFocus?.current ||
        root.querySelector<HTMLElement>("[data-autofocus]") ||
        getFocusable(root)[0] ||
        root;
      target.focus({ preventScroll: true });
    });
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      if (isTop && !isTop()) return;
      const root = ref.current;
      if (!root) return;
      const items = getFocusable(root);
      if (items.length === 0) {
        e.preventDefault();
        root.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const current = document.activeElement as HTMLElement | null;
      if (!current || !root.contains(current)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && current === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && current === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("keydown", onKey);
      if (returnFocus && opener && typeof opener.focus === "function" && document.contains(opener)) {
        opener.focus({ preventScroll: true });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
}

// ---------- media query ----------
export function useMediaQuery(query: string, ssrDefault = false) {
  const [matches, setMatches] = useState(ssrDefault);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const update = () => setMatches(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, [query]);
  return matches;
}

/** True at the `lg` breakpoint (1024px) and up, where the sidebar is docked instead of a drawer. */
export const useIsDesktop = () => useMediaQuery("(min-width: 1024px)", true);
